/**
 * Workflow Chaining Diagram
 *
 * Visual representation of the complete workflow chaining: Commands -> Events -> Subscriptions -> Workflows
 * Interactive: Click on any node to filter to the connected chain. Search to find specific items.
 */

import React, { useState, useMemo } from 'react';
import { Box, Paper, Typography, Chip, Alert, IconButton, Tooltip, TextField, InputAdornment } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useChainOverview } from './hooks/useWorkflowChaining';
import { getModuleColors } from './utils/moduleColors';

interface HighlightedChain {
  commands: Set<string>;
  events: Set<string>;
  subscriptions: Set<string>;
  workflows: Set<string>;
}

export const WorkflowChainingDiagram: React.FC = () => {
  const { data: overview, isLoading, error } = useChainOverview();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedNodeType, setSelectedNodeType] = useState<'command' | 'event' | 'subscription' | 'workflow' | null>(null);
  const [searchText, setSearchText] = useState('');

  // Build connection maps for quick lookup
  const connectionMaps = useMemo(() => {
    if (!overview) return null;

    // Build event ID lookup map (short name -> full ETO name)
    const eventShortToFull = new Map<string, string>();
    const eventFullToShort = new Map<string, string>();
    overview.events.forEach(evt => {
      eventShortToFull.set(evt.eventType, evt.id);
      eventFullToShort.set(evt.id, evt.eventType);
    });

    // Normalize event name (handle both short and full names)
    const normalizeEventName = (name: string): string => {
      return eventShortToFull.get(name) || name;
    };

    // Command -> Events map
    const commandToEvents = new Map<string, string[]>();
    overview.commands.forEach(cmd => {
      const normalizedEvents = cmd.emitsEvents.map(evt => normalizeEventName(evt));
      commandToEvents.set(cmd.id, normalizedEvents);
    });

    // Event -> Subscriptions map
    const eventToSubscriptions = new Map<string, string[]>();
    overview.subscriptions.forEach(sub => {
      const existing = eventToSubscriptions.get(sub.eventType) || [];
      eventToSubscriptions.set(sub.eventType, [...existing, sub.id]);
    });

    // Subscription -> Workflow map
    const subscriptionToWorkflow = new Map<string, string>();
    overview.subscriptions.forEach(sub => {
      subscriptionToWorkflow.set(sub.id, sub.workflowName);
    });

    // Reverse maps
    const eventToCommands = new Map<string, string[]>();
    overview.commands.forEach(cmd => {
      cmd.emitsEvents.forEach(evt => {
        const normalizedEvt = normalizeEventName(evt);
        const existing = eventToCommands.get(normalizedEvt) || [];
        eventToCommands.set(normalizedEvt, [...existing, cmd.id]);
      });
    });

    const subscriptionToEvent = new Map<string, string>();
    overview.subscriptions.forEach(sub => {
      subscriptionToEvent.set(sub.id, sub.eventType);
    });

    const workflowToSubscriptions = new Map<string, string[]>();
    overview.subscriptions.forEach(sub => {
      const existing = workflowToSubscriptions.get(sub.workflowName) || [];
      workflowToSubscriptions.set(sub.workflowName, [...existing, sub.id]);
    });

    return {
      commandToEvents,
      eventToSubscriptions,
      subscriptionToWorkflow,
      eventToCommands,
      subscriptionToEvent,
      workflowToSubscriptions,
      eventShortToFull,
      eventFullToShort,
    };
  }, [overview]);

  // Calculate highlighted chain based on selected node
  const highlightedChain = useMemo((): HighlightedChain | null => {
    if (!selectedNodeId || !selectedNodeType || !connectionMaps || !overview) return null;

    const chain: HighlightedChain = {
      commands: new Set(),
      events: new Set(),
      subscriptions: new Set(),
      workflows: new Set(),
    };

    if (selectedNodeType === 'command') {
      chain.commands.add(selectedNodeId);
      const events = connectionMaps.commandToEvents.get(selectedNodeId) || [];
      events.forEach(evt => {
        chain.events.add(evt);
        const shortName = connectionMaps.eventFullToShort.get(evt);
        if (shortName) chain.events.add(shortName);
        const subs = connectionMaps.eventToSubscriptions.get(evt) || [];
        subs.forEach(sub => {
          chain.subscriptions.add(sub);
          const wf = connectionMaps.subscriptionToWorkflow.get(sub);
          if (wf) chain.workflows.add(wf);
        });
      });
    } else if (selectedNodeType === 'event') {
      chain.events.add(selectedNodeId);
      const shortName = connectionMaps.eventFullToShort.get(selectedNodeId);
      if (shortName) chain.events.add(shortName);
      const commands = connectionMaps.eventToCommands.get(selectedNodeId) || [];
      commands.forEach(cmd => chain.commands.add(cmd));
      const subs = connectionMaps.eventToSubscriptions.get(selectedNodeId) || [];
      subs.forEach(sub => {
        chain.subscriptions.add(sub);
        const wf = connectionMaps.subscriptionToWorkflow.get(sub);
        if (wf) chain.workflows.add(wf);
      });
    } else if (selectedNodeType === 'subscription') {
      chain.subscriptions.add(selectedNodeId);
      const evt = connectionMaps.subscriptionToEvent.get(selectedNodeId);
      if (evt) {
        chain.events.add(evt);
        const shortName = connectionMaps.eventFullToShort.get(evt);
        if (shortName) chain.events.add(shortName);
        const commands = connectionMaps.eventToCommands.get(evt) || [];
        commands.forEach(cmd => chain.commands.add(cmd));
      }
      const wf = connectionMaps.subscriptionToWorkflow.get(selectedNodeId);
      if (wf) chain.workflows.add(wf);
    } else if (selectedNodeType === 'workflow') {
      chain.workflows.add(selectedNodeId);
      const subs = connectionMaps.workflowToSubscriptions.get(selectedNodeId) || [];
      subs.forEach(sub => {
        chain.subscriptions.add(sub);
        const evt = connectionMaps.subscriptionToEvent.get(sub);
        if (evt) {
          chain.events.add(evt);
          const shortName = connectionMaps.eventFullToShort.get(evt);
          if (shortName) chain.events.add(shortName);
          const commands = connectionMaps.eventToCommands.get(evt) || [];
          commands.forEach(cmd => chain.commands.add(cmd));
        }
      });
    }

    return chain;
  }, [selectedNodeId, selectedNodeType, connectionMaps, overview]);

  const handleNodeClick = (nodeId: string, nodeType: 'command' | 'event' | 'subscription' | 'workflow') => {
    if (selectedNodeId === nodeId && selectedNodeType === nodeType) {
      setSelectedNodeId(null);
      setSelectedNodeType(null);
    } else {
      setSelectedNodeId(nodeId);
      setSelectedNodeType(nodeType);
    }
  };

  const handleClearSelection = () => {
    setSelectedNodeId(null);
    setSelectedNodeType(null);
    setSearchText('');
  };

  // Compute connection counts for each item
  const counts = useMemo(() => {
    if (!connectionMaps) return null;

    const commandCounts = new Map<string, { events: number; subscriptions: number; workflows: number }>();
    connectionMaps.commandToEvents.forEach((events, cmdId) => {
      const subs = new Set<string>();
      const wfs = new Set<string>();
      events.forEach(evt => {
        (connectionMaps.eventToSubscriptions.get(evt) || []).forEach(s => {
          subs.add(s);
          const wf = connectionMaps.subscriptionToWorkflow.get(s);
          if (wf) wfs.add(wf);
        });
      });
      commandCounts.set(cmdId, { events: events.length, subscriptions: subs.size, workflows: wfs.size });
    });

    const eventCounts = new Map<string, { commands: number; subscriptions: number }>();
    overview?.events.forEach(evt => {
      const cmds = (connectionMaps.eventToCommands.get(evt.id) || []).length;
      const subs = (connectionMaps.eventToSubscriptions.get(evt.id) || []).length;
      eventCounts.set(evt.id, { commands: cmds, subscriptions: subs });
    });

    const workflowCounts = new Map<string, { subscriptions: number }>();
    connectionMaps.workflowToSubscriptions.forEach((subs, wfName) => {
      workflowCounts.set(wfName, { subscriptions: subs.length });
    });

    return { commandCounts, eventCounts, workflowCounts };
  }, [connectionMaps, overview]);

  // Deduplicate workflows: pick published version per definitionId, else latest
  const uniqueWorkflows = useMemo(() => {
    if (!overview) return [];
    const byDefId = new Map<string, typeof overview.workflows[0]>();
    for (const wf of overview.workflows) {
      const existing = byDefId.get(wf.definitionId);
      if (!existing || wf.isPublished || (!existing.isPublished && wf.version > existing.version)) {
        byDefId.set(wf.definitionId, wf);
      }
    }
    return Array.from(byDefId.values());
  }, [overview]);

  // Filter items by search text and chain selection
  const filteredData = useMemo(() => {
    if (!overview) return null;
    const search = searchText.toLowerCase();

    const matchesSearch = (text: string) => !search || text.toLowerCase().includes(search);
    const inChain = (id: string, type: keyof HighlightedChain) =>
      !highlightedChain || highlightedChain[type].has(id);

    return {
      commands: overview.commands.filter(
        cmd => inChain(cmd.id, 'commands') && matchesSearch(`${cmd.name} ${cmd.module} ${cmd.entity}`)
      ),
      events: overview.events.filter(
        evt => inChain(evt.id, 'events') && matchesSearch(`${evt.eventType} ${evt.module} ${evt.entity}`)
      ),
      subscriptions: overview.subscriptions.filter(
        sub => inChain(sub.id, 'subscriptions') && matchesSearch(`${sub.name} ${sub.eventType} ${sub.workflowName}`)
      ),
      workflows: uniqueWorkflows.filter(
        wf => inChain(wf.definitionId, 'workflows') && matchesSearch(`${wf.name} ${wf.definitionId}`)
      ),
    };
  }, [overview, uniqueWorkflows, highlightedChain, searchText]);

  if (isLoading) {
    return <Typography>Loading diagram...</Typography>;
  }

  if (error) {
    return <Alert severity="error">Failed to load chaining overview</Alert>;
  }

  if (!overview || !filteredData) {
    return <Alert severity="info">No chaining data available</Alert>;
  }

  const hasActiveFilter = !!selectedNodeId || !!searchText;

  const columnSx = {
    flex: 1,
    minWidth: 220,
    maxHeight: 500,
    overflowY: 'auto' as const,
    pr: 0.5,
  };

  const getCardSx = (isSelected: boolean, borderColor: string, bgColor: string) => ({
    px: 1.5,
    py: 1,
    mb: 0.5,
    borderLeft: `4px solid ${borderColor}`,
    backgroundColor: bgColor,
    cursor: 'pointer',
    transition: 'all 0.15s',
    boxShadow: isSelected ? 3 : 0,
    '&:hover': {
      boxShadow: 2,
    },
  });

  return (
    <Paper sx={{ p: 2, overflowX: 'auto' }}>
      {/* Header with search and clear */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ flexShrink: 0 }}>
          Workflow Chaining Overview
        </Typography>
        <TextField
          size="small"
          placeholder="Filter..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: 250 }}
        />
        {hasActiveFilter && (
          <Tooltip title="Clear filter">
            <IconButton size="small" onClick={handleClearSelection}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flexShrink: 0 }}>
          {filteredData.commands.length} commands, {filteredData.events.length} events, {filteredData.subscriptions.length} subscriptions, {filteredData.workflows.length} workflows
        </Typography>
      </Box>

      {/* Four-column layout with independent scroll */}
      <Box sx={{ display: 'flex', gap: 2, minWidth: 1000 }}>
        {/* Commands Column */}
        <Box sx={columnSx}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, pb: 0.5 }}>
            Commands ({filteredData.commands.length})
          </Typography>
          {filteredData.commands.map((cmd) => {
            const colors = getModuleColors(cmd.module);
            const isSelected = selectedNodeId === cmd.id && selectedNodeType === 'command';
            const c = counts?.commandCounts.get(cmd.id);
            return (
              <Paper
                key={cmd.id}
                onClick={() => handleNodeClick(cmd.id, 'command')}
                sx={getCardSx(isSelected, colors.border, colors.bg)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>
                    {cmd.name}
                  </Typography>
                  <Chip
                    label={cmd.module}
                    size="small"
                    sx={{ backgroundColor: colors.border, color: 'white', height: 18, fontSize: '0.65rem' }}
                  />
                </Box>
                {c && (
                  <Typography variant="caption" color="text.secondary">
                    {c.events} event{c.events !== 1 ? 's' : ''} &middot; {c.subscriptions} sub{c.subscriptions !== 1 ? 's' : ''} &middot; {c.workflows} wf{c.workflows !== 1 ? 's' : ''}
                  </Typography>
                )}
              </Paper>
            );
          })}
          {filteredData.commands.length === 0 && (
            <Typography variant="caption" color="text.secondary">No matching commands</Typography>
          )}
        </Box>

        {/* Events Column */}
        <Box sx={columnSx}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, pb: 0.5 }}>
            Events ({filteredData.events.length})
          </Typography>
          {filteredData.events.map((event) => {
            const colors = getModuleColors(event.module);
            const isSelected = selectedNodeId === event.id && selectedNodeType === 'event';
            const c = counts?.eventCounts.get(event.id);
            return (
              <Paper
                key={event.id}
                onClick={() => handleNodeClick(event.id, 'event')}
                sx={getCardSx(isSelected, colors.border, colors.bg)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>
                    {event.eventType}
                  </Typography>
                  <Chip
                    label={event.module}
                    size="small"
                    sx={{ backgroundColor: colors.border, color: 'white', height: 18, fontSize: '0.65rem' }}
                  />
                </Box>
                {c && (c.commands > 0 || c.subscriptions > 0) && (
                  <Typography variant="caption" color="text.secondary">
                    {c.commands} cmd{c.commands !== 1 ? 's' : ''} &middot; {c.subscriptions} sub{c.subscriptions !== 1 ? 's' : ''}
                  </Typography>
                )}
              </Paper>
            );
          })}
          {filteredData.events.length === 0 && (
            <Typography variant="caption" color="text.secondary">No matching events</Typography>
          )}
        </Box>

        {/* Subscriptions Column */}
        <Box sx={columnSx}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, pb: 0.5 }}>
            Subscriptions ({filteredData.subscriptions.length})
          </Typography>
          {filteredData.subscriptions.map((sub) => {
            const isSelected = selectedNodeId === sub.id && selectedNodeType === 'subscription';
            return (
              <Paper
                key={sub.id}
                onClick={() => handleNodeClick(sub.id, 'subscription')}
                sx={getCardSx(isSelected,
                  sub.isEnabled ? '#22C55E' : '#9CA3AF',
                  sub.isEnabled ? '#F0FDF4' : '#F3F4F6'
                )}
              >
                <Typography variant="body2" fontWeight="bold">
                  {sub.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {sub.eventType} &rarr; {sub.workflowName}
                </Typography>
                {sub.isDslDefined && (
                  <Chip label="DSL" size="small" sx={{ height: 16, fontSize: '0.6rem', ml: 1 }} />
                )}
              </Paper>
            );
          })}
          {filteredData.subscriptions.length === 0 && (
            <Typography variant="caption" color="text.secondary">No matching subscriptions</Typography>
          )}
        </Box>

        {/* Workflows Column */}
        <Box sx={columnSx}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, pb: 0.5 }}>
            Workflows ({filteredData.workflows.length})
          </Typography>
          {filteredData.workflows.map((wf) => {
            const colors = wf.module ? getModuleColors(wf.module) : { border: '#6B7280', bg: '#F9FAFB' };
            const isSelected = selectedNodeId === wf.definitionId && selectedNodeType === 'workflow';
            const c = counts?.workflowCounts.get(wf.definitionId);
            return (
              <Paper
                key={wf.id}
                onClick={() => handleNodeClick(wf.definitionId, 'workflow')}
                sx={getCardSx(isSelected, colors.border, colors.bg)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ flex: 1 }}>
                    {wf.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    v{wf.version}
                  </Typography>
                </Box>
                {c && c.subscriptions > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {c.subscriptions} sub{c.subscriptions !== 1 ? 's' : ''}
                  </Typography>
                )}
              </Paper>
            );
          })}
          {filteredData.workflows.length === 0 && (
            <Typography variant="caption" color="text.secondary">No matching workflows</Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
